/**
 * Round-based battle simulator (GDD §5 step 4).
 *
 * Model: up to 6 rounds. Each round, every surviving unit fires once at a
 * random enemy unit. Shields absorb first and regenerate every round; overflow
 * damage burns hull, which never regenerates. A unit whose hull reaches 0 is
 * destroyed at the end of the round. Deterministic under a seed.
 *
 * Destroyed units leave 30 % of their titan+silicate cost as orbital debris,
 * which feeds the GDD §3 moon-formation rule.
 */

import type { BattleParty, BattleResult, UnitKey } from './types.js';
import { UNITS } from './data/units.js';
import { moonChance } from './formulas.js';
import { createRng } from './rng.js';

const MAX_ROUNDS = 6;
const DEBRIS_RATE = 0.3;

interface LiveUnit {
  key: UnitKey;
  hull: number;
  shieldMax: number;
  damage: number;
}

/** Capital ships benefit from the war-talent damage band (GDD §2 tier 6–10). */
const CAPITALS: ReadonlySet<UnitKey> = new Set(['cruiser', 'battleship', 'voidPhantom', 'moonBreaker']);

function expand(party: BattleParty): LiveUnit[] {
  const shieldFactor = 1 + 0.1 * (party.shieldTech ?? 0);
  const out: LiveUnit[] = [];
  for (const [key, count] of Object.entries(party.units) as [UnitKey, number][]) {
    const def = UNITS[key];
    if (!def || count <= 0) continue;
    const dmgBonus = 1 + (CAPITALS.has(key) ? (party.damageBonus ?? 0) : 0);
    for (let i = 0; i < count; i++) {
      out.push({
        key,
        hull: def.hull,
        shieldMax: Math.round(def.shield * shieldFactor),
        damage: def.damage * dmgBonus,
      });
    }
  }
  return out;
}

function countByKey(units: LiveUnit[]): Partial<Record<UnitKey, number>> {
  const out: Partial<Record<UnitKey, number>> = {};
  for (const u of units) out[u.key] = (out[u.key] ?? 0) + 1;
  return out;
}

function losses(
  before: Partial<Record<UnitKey, number>>,
  after: Partial<Record<UnitKey, number>>,
): Partial<Record<UnitKey, number>> {
  const out: Partial<Record<UnitKey, number>> = {};
  for (const [key, n] of Object.entries(before) as [UnitKey, number][]) {
    const lost = n - (after[key] ?? 0);
    if (lost > 0) out[key] = lost;
  }
  return out;
}

export function simulateBattle(attacker: BattleParty, defender: BattleParty, seed: number): BattleResult {
  const rng = createRng(seed);
  let att = expand(attacker);
  let def = expand(defender);
  const attBefore = countByKey(att);
  const defBefore = countByKey(def);

  let round = 0;
  while (round < MAX_ROUNDS && att.length > 0 && def.length > 0) {
    round += 1;
    // Shields regenerate at the start of every round.
    const attShield = att.map((u) => u.shieldMax);
    const defShield = def.map((u) => u.shieldMax);

    // Both sides fire simultaneously — casualties are removed after the round.
    for (const unit of att) {
      const target = rng.int(0, def.length - 1);
      const absorbed = Math.min(defShield[target]!, unit.damage);
      defShield[target]! -= absorbed;
      def[target]!.hull -= unit.damage - absorbed;
    }
    for (const unit of def) {
      const target = rng.int(0, att.length - 1);
      const absorbed = Math.min(attShield[target]!, unit.damage);
      attShield[target]! -= absorbed;
      att[target]!.hull -= unit.damage - absorbed;
    }

    att = att.filter((u) => u.hull > 0);
    def = def.filter((u) => u.hull > 0);
  }

  const attackerLosses = losses(attBefore, countByKey(att));
  const defenderLosses = losses(defBefore, countByKey(def));

  let debrisTitan = 0;
  let debrisSilicate = 0;
  for (const side of [attackerLosses, defenderLosses]) {
    for (const [key, n] of Object.entries(side) as [UnitKey, number][]) {
      const def_ = UNITS[key];
      debrisTitan += (def_.cost.titan ?? 0) * n * DEBRIS_RATE;
      debrisSilicate += (def_.cost.silicate ?? 0) * n * DEBRIS_RATE;
    }
  }
  debrisTitan = Math.round(debrisTitan);
  debrisSilicate = Math.round(debrisSilicate);

  const winner = att.length > 0 && def.length === 0
    ? 'attacker'
    : def.length > 0 && att.length === 0
      ? 'defender'
      : 'draw';

  return {
    winner,
    rounds: round,
    attackerLosses,
    defenderLosses,
    debris: { titan: debrisTitan, silicate: debrisSilicate },
    moonChance: moonChance(debrisTitan, debrisSilicate),
  };
}
